/**
 * workLifecycle — the lifecycle model the Work surface reasons about, keyed to
 * the REAL `WorkItemPhase` union from `@yagni/shared`. The stage vocabulary is
 * not here: it is the pipeline's six words (`workStage.ts`, from shared), and
 * the list's grouping and order live with the views (`workViews.ts`). What
 * lives here is the case file's status pill over the five row states, and the
 * live-run reconciliation.
 */
import type { Run, WorkItem } from "@yagni/shared";
import { readSplitPlace, splitMergeWords, splitWaitWords } from "./splitPlace.js";
import { formatRelative } from "../lib/relativeTime.js";
import {
  itemStopWords,
  pipelineStageOf,
  QA_RESULT_UNKNOWN,
  qaResultWords,
  reviewWaitFactsOf,
  reviewWaitOf,
  reviewWaitWords,
  rowStateOf,
} from "./workStage.js";

function sentenceCase(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * HONEST STATUS — the one place the Work surface derives "what is this item
 * actually doing right now" from the fields it already has. No fake "running on
 * its own": a card reads `working` only when a run is genuinely live
 * (`currentRunId` for execution, `latestPlanningRunId` while drafting), and
 * otherwise reads `scheduled` (a due date is set) or `parked` (nothing is).
 *
 * Precedence: needs-you (an OPEN gate — {@link isNeedsYou}, gate-truth, never
 * phase alone) → working (a live run) → terminal → scheduled → parked. Within
 * the needs-you branch the label stays phase-specific because each needs-you
 * phase mirrors its gate kind (an agent-proposed item carries an
 * `accept_proposal` gate) and deserves its wording over the generic
 * "approve plan".
 */
export type WorkStatusTone = "needsYou" | "working" | "planning" | "scheduled" | "parked" | "done";

export interface WorkItemStatus {
  tone: WorkStatusTone;
  label: string;
}

export function workItemStatus(item: WorkItem): WorkItemStatus {
  const phase = item.phase;
  const isReview = item.kind === "review";
  // ONE derivation of "what is this doing", shared with the list row's own
  // sentence (`workStage.rowStateLine`): the case-file header and the row that
  // opened it read the same five states, so they cannot disagree about whether
  // a build is working, stopped, or waiting on a person. This function is only
  // the pill vocabulary for those states.
  const state = rowStateOf(item);

  // 1. Needs you — an OPEN gate on the item (gate-truth: a needs-you PHASE
  //    without a gate is the Team's parked ask, not your call). The label is
  //    phase-specific because each needs-you phase mirrors its gate kind;
  //    a review case sits at `executing` its whole life, so its gate is the
  //    held verdict — never the commissioned "approve plan" default.
  if (state === "waiting") {
    if (isReview) return { tone: "needsYou", label: "Waiting: review result" };
    if (phase === "needs_input") return { tone: "needsYou", label: "Waiting: your answer" };
    if (phase === "final_review") return { tone: "needsYou", label: "Waiting: review result" };
    if (phase === "proposed") return { tone: "needsYou", label: "Waiting: accept proposal" };
    return { tone: "needsYou", label: "Waiting: approve plan" };
  }

  // 2. Terminal.
  if (state === "settled") {
    // What the check found, never "check complete" (YAG-1079).
    if (pipelineStageOf(item) === "qa") {
      return { tone: "done", label: sentenceCase(qaResultWords(item) ?? QA_RESULT_UNKNOWN) };
    }
    return phase === "dismissed"
      ? { tone: "done", label: "Closed" }
      : { tone: "done", label: "Done" };
  }

  // 3. Working — a run is live right now (not merely an in-flight phase).
  //    A review case's live run is the reviewer itself (the run claims the
  //    case item), so "Reviewing now" says what is actually running.
  if (state === "working") {
    if (isReview) return { tone: "working", label: "Reviewing now" };
    if (phase === "planning") return { tone: "working", label: "Drafting plan" };
    return { tone: "working", label: "Working now" };
  }

  // 4. Stopped — the last run ended badly and nothing replaced it. This used
  //    to read "Parked. Nothing scheduled.", which is true of the schedule and
  //    a lie about the item: nobody parked it, it fell over. The reason comes
  //    from the same map the list row prints, so both say the same words.
  if (state === "stopped") {
    const words = itemStopWords(item);
    return {
      tone: "parked",
      label: words === "stopped" ? "Stopped" : `Stopped: ${words}`,
    };
  }

  // 5. Queued in Review. A review case, or a ticket whose pull request is in
  //    Review, waits on something other than a schedule: the reviewer, a
  //    push, or a person's merge. The row reads the same rule.
  if (isReview || (pipelineStageOf(item) === "review" && item.pullRequest)) {
    const wait = reviewWaitOf(reviewWaitFactsOf(item));
    // A split's ticket built ahead whose draft waits on the one before it to
    // merge (YAG-1112): built, and only its merge waits.
    const splitMerge = wait === "draft" ? splitMergeWords(readSplitPlace(item)) : null;
    if (splitMerge) return { tone: "parked", label: `Built, ${splitMerge}` };
    if (wait || isReview) return { tone: "parked", label: reviewWaitWords(wait) };
  }

  // 5.5 The activation item — onboarding's standing invitation on the work
  //     spine. It is never honestly "parked" (it advances the moment the user
  //     shows up), so it reads as a warm invitation instead of the fallthrough
  //     below. Gate-truth above is untouched: with an open gate it needs you.
  if (item.isActivation) return { tone: "scheduled", label: "Ready when you are" };

  // 5.6 A split's ticket waiting on an earlier one's merge (ENG-3033): it
  //     starts on its own when that merges, and says which one it waits on.
  const splitWait = splitWaitWords(readSplitPlace(item));
  if (splitWait) return { tone: "scheduled", label: sentenceCase(splitWait) };

  // 6. An in-flight phase with no live run: scheduled if a follow-up is due,
  //    otherwise honestly parked. A dueAt in the past means the item is already
  //    eligible for an evaluator pass — read "Queued for next pass" so the
  //    owner does not think they must manually start it.
  if (item.dueAt) {
    const due = new Date(item.dueAt).getTime();
    if (Number.isFinite(due) && due <= Date.now())
      return { tone: "scheduled", label: "Queued for next pass" };
    return { tone: "scheduled", label: `Scheduled: ${formatRelative(item.dueAt)}` };
  }
  return { tone: "parked", label: "Parked. Nothing scheduled." };
}

/** Run statuses that mean the run has ENDED, one way or another. */
const ENDED_RUN_STATUSES: ReadonlySet<string> = new Set([
  "complete",
  "partial",
  "failed",
  "dismissed",
]);

/**
 * The item as the runs roll-up proves it.
 *
 * `currentRunId` is the item's claim that a run is live. The roll-up beside
 * it is read more often (it polls while a run wears the running tone, and a
 * live flip reloads it), so the two can disagree for a while: a Proctor
 * review finished, its run went `complete`, the case went `done`, and the
 * open case file still held the item it loaded mid-review, header reading
 * "Reviewing now" over a roll-up that said complete (staging, NEARM-397,
 * 2026-09-17).
 *
 * `finishRun` clears the pointer on every terminal path, so a pointer at a
 * run the roll-up shows as ENDED is proof the item is stale, and the pointer
 * is dropped. A run still going, paused, or not yet in the roll-up leaves the
 * item alone: absence is not evidence, and a paused run is still the item's
 * current work. While the roll-up is loading there is nothing to compare
 * against, so the item is trusted as read. Returns the same object when
 * nothing changes, so callers can tell.
 */
export function reconcileLiveRun<T extends Pick<WorkItem, "currentRunId">>(
  item: T,
  runs: ReadonlyArray<Pick<Run, "id" | "status">>,
  runsLoading: boolean,
): T {
  if (runsLoading || !item.currentRunId) return item;
  const named = runs.find((run) => run.id === item.currentRunId);
  if (!named || !ENDED_RUN_STATUSES.has(named.status)) return item;
  return { ...item, currentRunId: null };
}

/**
 * The other direction: a build the roll-up shows running that the item does
 * not name yet. A fix round starts on the build item while the list still
 * holds the item it read waiting in Review, and the sheet read "Review ·
 * queued" over Wright's live round (MILD-235, 2026-10-12). The roll-up is the
 * fresher read, and a live execution run in it is proof, so the item reads as
 * working on that run. Planning runs are left alone: the item claims them
 * through `latestPlanningRunId`. Returns the same object when nothing changes.
 */
export function adoptLiveRun<T extends Pick<WorkItem, "currentRunId" | "activeRun" | "phase">>(
  item: T,
  runs: ReadonlyArray<Pick<Run, "id" | "status" | "purpose" | "attemptNo" | "startedAt">>,
): T {
  if (item.currentRunId) return item;
  if (item.phase === "done" || item.phase === "dismissed" || item.phase === "split") return item;
  const live = runs.find(
    (run) => run.purpose === "execution" && (run.status === "running" || run.status === "scoping"),
  );
  if (!live) return item;
  return {
    ...item,
    currentRunId: live.id,
    activeRun: item.activeRun ?? {
      id: live.id,
      status: live.status,
      attemptNo: live.attemptNo,
      startedAt: live.startedAt,
      lastEventAt: null,
      budgetMinutes: null,
      lastEvent: null,
    },
  };
}
